<template>
  <WdsModal
    ref="modal"
    :show="show"
    :z-index="4000"
    custom-size="650px"
    data-test="invite-link-modal"
    @cancel="handleCancel"
  >
    <template #header>
      <div class="tw-flex tw-items-center tw-gap-sm">
        <WdsIcon :name="WdsIconNameEnum.LINK" :size="20" />
        <WdsHeading type="title2">{{ $t('Get invitation link') }}</WdsHeading>
      </div>
    </template>

    <template #default>
      <WdsAlert v-if="generateInviteLinkError" error class="tw-mb-4">
        {{ $t('An error occurred') }}
      </WdsAlert>

      <div v-if="!loading">
        <WdsStack direction-column gap-lg>
          <!-- Intro text -->
          <WdsText secondary>
            {{
              $t(
                'Share links to easily invite new talent to Worksome. Their profiles will appear in your talent pool once they sign up.',
              )
            }}
          </WdsText>

          <!-- Personal Invite Link Section -->
          <div class="personal-link-section">
            <WdsStack direction-column gap-sm>
              <div class="tw-flex tw-items-center tw-justify-between">
                <div class="tw-flex tw-items-center tw-gap-sm">
                  <WdsIcon :name="WdsIconNameEnum.STAR" :size="18" />
                  <WdsHeading type="title4">{{ $t('Your personal link') }}</WdsHeading>
                </div>
                <WdsButtonGhost
                  v-if="personalInviteLink"
                  data-test="regenerate-personal-link"
                  class="tw-flex tw-items-center tw-gap-xs"
                  @click="regeneratePersonalLink"
                >
                  <WdsStack class="reset-button" gap-xs justify-center align-center>
                    <WdsIcon :name="WdsIconNameEnum.RENEW" :size="16" />
                    {{ $t('Reset') }}
                  </WdsStack>
                </WdsButtonGhost>
              </div>

              <WdsText secondary>
                {{ $t('For trusted referrals and direct hires. Profiles are auto-approved.') }}
              </WdsText>

              <!-- Reset Confirmation Box for Personal Link -->
              <div v-if="showPersonalResetConfirmation" class="reset-confirmation-box">
                <WdsStack direction-column gap-sm align-center>
                  <WdsIcon :name="WdsIconNameEnum.INFO" :size="18" class="warning-icon" />
                  <WdsText align="center">
                    <div class="tw-text-red-600">
                      {{
                        $t(
                          "Resetting will deactivate the current link for anyone you've shared it with and generate a new one.",
                        )
                      }}
                    </div>
                  </WdsText>
                  <WdsStack gap-sm justify-center>
                    <WdsButtonGhost
                      data-test="cancel-personal-reset"
                      class="confirmation-button"
                      @click="showPersonalResetConfirmation = false"
                    >
                      {{ $t('Cancel') }}
                    </WdsButtonGhost>
                    <WdsButton
                      data-test="confirm-personal-reset"
                      class="confirmation-button"
                      primary
                      @click="confirmRegeneratePersonalLink"
                    >
                      {{ $t('Confirm') }}
                    </WdsButton>
                  </WdsStack>
                </WdsStack>
              </div>

              <!-- Personal Link Input -->
              <div v-else-if="personalInviteLink" class="input-with-copy">
                <WdsInput
                  :model-value="personalInviteLink"
                  readonly
                  data-test="personal-invite-url"
                />
                <button
                  class="copy-button-inside"
                  data-test="copy-personal-url"
                  @click="copy(personalInviteLink)"
                >
                  <WdsIcon :name="WdsIconNameEnum.COPY" :size="16" />
                </button>
              </div>
            </WdsStack>
          </div>

          <!-- Company Public Link Section -->
          <div class="public-link-section">
            <WdsStack direction-column gap-sm>
              <div class="tw-flex tw-items-center tw-justify-between">
                <div class="tw-flex tw-items-center tw-gap-sm">
                  <WdsIcon :name="WdsIconNameEnum.BROADCAST" :size="18" />
                  <WdsHeading type="title4">{{ $t('Public link') }}</WdsHeading>
                </div>
                <WdsButtonGhost
                  v-if="companyInviteUrl"
                  data-test="regenerate-company-link"
                  class="tw-flex tw-items-center tw-gap-xs"
                  @click="regenerateCompanyLink"
                >
                  <WdsStack class="reset-button" gap-xs justify-center align-center>
                    <WdsIcon :name="WdsIconNameEnum.RENEW" :size="16" />
                    {{ $t('Reset') }}
                  </WdsStack>
                </WdsButtonGhost>
              </div>

              <WdsText secondary>
                {{ $t('For sharing broadly. Each profile needs to be reviewed and approved.') }}
              </WdsText>

              <!-- Reset Confirmation Box for Company Link -->
              <div v-if="showCompanyResetConfirmation" class="reset-confirmation-box">
                <WdsStack direction-column gap-sm align-center>
                  <WdsIcon :name="WdsIconNameEnum.INFO" :size="18" class="warning-icon" />
                  <WdsText align="center">
                    <div class="tw-text-red-600">
                      {{
                        $t(
                          "Resetting will deactivate the current link for anyone you've shared it with and generate a new one.",
                        )
                      }}
                    </div>
                  </WdsText>
                  <WdsStack gap-sm justify-center>
                    <WdsButtonGhost
                      data-test="cancel-company-reset"
                      class="confirmation-button"
                      @click="showCompanyResetConfirmation = false"
                    >
                      {{ $t('Cancel') }}
                    </WdsButtonGhost>
                    <WdsButton
                      data-test="confirm-company-reset"
                      class="confirmation-button"
                      primary
                      @click="confirmRegenerateCompanyLink"
                    >
                      {{ $t('Confirm') }}
                    </WdsButton>
                  </WdsStack>
                </WdsStack>
              </div>

              <!-- Company Link Input -->
              <div v-else-if="companyInviteUrl" class="input-with-copy">
                <WdsInput :model-value="companyInviteUrl" readonly data-test="company-invite-url" />
                <WdsButtonGhost
                  class="copy-button-inside"
                  data-test="copy-company-url"
                  @click="copy(companyInviteUrl)"
                >
                  <WdsIcon :name="WdsIconNameEnum.COPY" :size="16" />
                </WdsButtonGhost>
              </div>
            </WdsStack>
          </div>

          <!-- Note -->
          <WdsText secondary>
            {{ $t("Note: Resetting will deactivate the link for anyone you've shared it with.") }}
          </WdsText>
        </WdsStack>
      </div>
    </template>
  </WdsModal>
</template>

<script setup>
import gql from 'graphql-tag'
import { useClipboard } from '@vueuse/core'
import { ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  WdsAlert,
  WdsModal,
  WdsStack,
  WdsButtonGhost,
  WdsText,
  WdsHeading,
  WdsIcon,
  WdsIconNameEnum,
  WdsInput,
  WdsButton,
} from '@worksome/wds-components'
import { $t } from '@/composables/translation'
import { useToast } from '@/composables/useToast'

defineProps({
  show: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['close'])

const { copy } = useClipboard()

const companyInviteUrl = ref(null)
const personalInviteLink = ref(null)
const showPersonalResetConfirmation = ref(false)
const showCompanyResetConfirmation = ref(false)

const toast = useToast()

// Query to get both links - auto-generates if they don't exist
const { onResult, loading } = useQuery(
  gql`
    query InviteLinksQuery($id: ID!) {
      company(id: $id) {
        contactInviteUrl
        personalInviteUrl
      }
    }
  `,
  {
    id: currentAccount.globalId,
  },
  {
    clientId: 'publicApi',
  },
)

onResult((result) => {
  if (result.loading === false) {
    companyInviteUrl.value = result.data.company.contactInviteUrl ?? null
    personalInviteLink.value = result.data.company.personalInviteUrl ?? null
  }
})

// Mutation for company link
const { mutate: generateCompanyInviteLink, error: generateInviteLinkError } = useMutation(
  gql`
    mutation GenerateCompanyInviteLinkMutation($company: ID!) {
      generateInviteLink(input: { company: $company }) {
        ... on Company {
          contactInviteUrl
        }
      }
    }
  `,
  {
    clientId: 'publicApi',
  },
)

// Mutation for personal link
const { mutate: generatePersonalInviteLink } = useMutation(
  gql`
    mutation GeneratePersonalInviteLinkMutation($company: ID!) {
      generatePersonalInviteLink(input: { company: $company })
    }
  `,
  {
    clientId: 'publicApi',
  },
)

const regenerateCompanyLink = () => {
  showCompanyResetConfirmation.value = true
}

const regeneratePersonalLink = () => {
  showPersonalResetConfirmation.value = true
}

const confirmRegenerateCompanyLink = async () => {
  const response = await generateCompanyInviteLink({
    company: currentAccount.globalId,
  })

  companyInviteUrl.value = response.data?.generateInviteLink?.contactInviteUrl ?? null
  showCompanyResetConfirmation.value = false

  toast.success($t('Link successfully reset'))
}

const confirmRegeneratePersonalLink = async () => {
  const response = await generatePersonalInviteLink({
    company: currentAccount.globalId,
  })

  personalInviteLink.value = response.data?.generatePersonalInviteLink ?? null
  showPersonalResetConfirmation.value = false

  toast.success($t('Link successfully reset'))
}

const handleCancel = () => {
  generateInviteLinkError.value = null
  emit('close')
}
</script>

<style scoped lang="scss">
.personal-link-section {
  @apply tw-p-4 tw-border tw-bg-indigo-50 tw-rounded-md;
}

.public-link-section {
  @apply tw-p-4 tw-border tw-bg-true-gray-50 tw-rounded-md;
}

.input-with-copy {
  @apply tw-relative tw-flex tw-items-center tw-w-full;

  :deep(.wds-input),
  :deep(.wds-input-wrapper),
  :deep(div[class*='wds-input']) {
    width: 100% !important;
    flex: 1 !important;
    max-width: none !important;
  }

  :deep(input) {
    padding-right: 48px !important;
    width: 100% !important;
  }
}

.copy-button-inside {
  @apply tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-p-1.5 tw-border-0 tw-rounded tw-bg-transparent tw-cursor-pointer tw-transition-all tw-duration-200 tw-text-gray-500 tw-mb-1 tw-z-10;

  &:hover {
    @apply tw-bg-gray-100 tw-text-gray-900;
  }

  &:active {
    @apply tw-bg-gray-200;
  }
}

.reset-button {
  @apply tw-text-gray-700;
}
.reset-button:hover {
  @apply tw-text-red-600;
}

.reset-confirmation-box {
  @apply tw-p-4 tw-border-solid tw-border tw-border-red-600 tw-bg-red-50 tw-rounded-md;
}

.warning-icon {
  @apply tw-text-red-600;
}

.confirmation-button {
  @apply tw-px-4 tw-flex tw-items-center tw-justify-center;
}

.warning-text {
  @apply tw-text-red-600;
}
</style>
